import { ContentCopy, Edit, Folder, Layers, LayersClear, Update } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MenuItemLink } from '~/components/link';
import { TooltipMenuItem } from '~/components/menu-item';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import type { ContentFolderView__ContentItem } from '../ContentFolderView.generated';

export interface ContentMenuContentItemProps {
  /** Set by the menu when this is its first child; this component has no element to take it. */
  autoFocus?: boolean;
  canUpdate: boolean;
  canUpgrade: boolean;
  item: ContentFolderView__ContentItem;
  onCopy: () => void;
  onMove: () => void;
  onOverlay: () => void;
  onRemoveOverlay: () => void;
  onUpgrade: () => void;
}

export const ContentMenuContentItem = ({
  autoFocus,
  canUpdate,
  canUpgrade,
  item,
  onCopy,
  onMove,
  onOverlay,
  onRemoveOverlay,
  onUpgrade,
}: ContentMenuContentItemProps) => {
  const { t } = useTranslation();
  const link = useLink();
  const location = useLocation();
  return (
    <>
      {!item.design && (
        <MenuItem autoFocus={autoFocus} onClick={onCopy}>
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>{t('pages.content.menu.duplicate')}</ListItemText>
        </MenuItem>
      )}

      <MenuItemLink
        autoFocus={autoFocus && !!item.design}
        to={
          item.design?.id
            ? link(`/studio/${item.design.designFolderId}/design/${item.design.id}`)
            : link(`/content/${item.nullableContentFolderId}/apps/${item.id}/settings`)
        }
        state={{ returnTo: location.fullPath }}
      >
        <ListItemIcon>
          <Edit />
        </ListItemIcon>
        <ListItemText>{t('common.actions.edit')}</ListItemText>
      </MenuItemLink>

      <MenuItem disabled={!canUpdate} onClick={onMove}>
        <ListItemIcon>
          <Folder />
        </ListItemIcon>
        <ListItemText>{t('pages.content.menu.move_to')}</ListItemText>
      </MenuItem>

      {item.overlay ? (
        <TooltipMenuItem
          disabled={!item.overlay.canDestroy.value}
          onClick={onRemoveOverlay}
          tooltip={item.overlay.canDestroy.reasons?.fullMessages.join(', ') ?? ''}
        >
          <ListItemIcon>
            <LayersClear />
          </ListItemIcon>
          <ListItemText>{t('pages.content.menu.remove_overlay')}</ListItemText>
        </TooltipMenuItem>
      ) : (
        <MenuItem onClick={onOverlay}>
          <ListItemIcon>
            <Layers />
          </ListItemIcon>
          <ListItemText>{t('pages.content.menu.use_as_overlay')}</ListItemText>
        </MenuItem>
      )}

      <MenuItem disabled={!canUpgrade} onClick={onUpgrade}>
        <ListItemIcon>
          <Update />
        </ListItemIcon>
        <ListItemText>{t('pages.content.menu.upgrade')}</ListItemText>
      </MenuItem>
    </>
  );
};
